import { useTranslation } from "react-i18next";
import { Badge, Icon, cn } from "@nexus/ui";
import type { IconName } from "@nexus/ui";
import type { PreflightReportStatus } from "../../types/preflight.types";

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

interface StatusMeta {
  variant: BadgeVariant;
  icon?: IconName;
  /** Colored dot per DESIGN.md `badge-status-*` (paired with text — KTD15). */
  dot?: boolean;
  /** Spin the icon for in-progress states. */
  spin?: boolean;
}

/** Report-status → label/color/icon map (KTD10). Color is always paired with text + icon (KTD15). */
const STATUS_META: Record<PreflightReportStatus, StatusMeta> = {
  QUEUED: { variant: "slate", icon: "clock" },
  ANALYZING: { variant: "status-in-production", icon: "loader", spin: true },
  COMPLETED: { variant: "status-connected" },
  COMPLETED_WITH_ERRORS: { variant: "status-in-production", dot: true },
  FAILED: { variant: "status-error", dot: true },
};

export interface StatusBadgeProps {
  status: PreflightReportStatus;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { t } = useTranslation();
  const meta = STATUS_META[status];
  return (
    <Badge variant={meta.variant} className={className} dot={meta.dot}>
      {meta.icon ? (
        <Icon
          name={meta.icon}
          size="xs"
          color="inherit"
          className={cn(meta.spin && "animate-spin")}
        />
      ) : null}
      {t(`preflight.status.${status}`)}
    </Badge>
  );
}
